/**
 * Definición centralizada de rutas HTTP del MVP.
 * Tanto el backend (Lambda) como el frontend (fetch client) importan de aquí
 * para evitar strings mágicos inconsistentes.
 */

export const ApiRoutes = {
  health: '/health',

  // Condominios
  condominios: '/condominios',
  condominioById: (id: string) => `/condominios/${id}`,

  // Feature flags (Superadmin)
  featureFlags: (tenantId: string) => `/admin/condominios/${tenantId}/feature-flags`,

  // Unidades
  unidades: (condoId: string) => `/condominios/${condoId}/unidades`,
  unidadById: (condoId: string, id: string) => `/condominios/${condoId}/unidades/${id}`,
  unidadHistorial: (condoId: string, id: string) =>
    `/condominios/${condoId}/unidades/${id}/historial`,
  unidadDesactivar: (condoId: string, id: string) =>
    `/condominios/${condoId}/unidades/${id}/desactivar`,

  // Residentes
  residentes: (condoId: string) => `/condominios/${condoId}/residentes`,
  residenteById: (condoId: string, id: string) => `/condominios/${condoId}/residentes/${id}`,
  residenteAsignarUnidad: (condoId: string, id: string) =>
    `/condominios/${condoId}/residentes/${id}/asignaciones`,
  residenteCerrarAsignacion: (condoId: string, id: string, asignacionId: string) =>
    `/condominios/${condoId}/residentes/${id}/asignaciones/${asignacionId}/cerrar`,
  residenteDesactivar: (condoId: string, id: string) =>
    `/condominios/${condoId}/residentes/${id}/desactivar`,

  // Vínculos vivienda ↔ accesorio
  vinculos: (condoId: string) => `/condominios/${condoId}/vinculos`,
  vinculoCerrar: (condoId: string, id: string) =>
    `/condominios/${condoId}/vinculos/${id}/cerrar`,

  // Fondo de reserva
  fondoReserva: (condoId: string) => `/condominios/${condoId}/fondo-reserva`,
  fondoReservaIngresos: (condoId: string) => `/condominios/${condoId}/fondo-reserva/ingresos`,
  fondoReservaEgresos: (condoId: string) => `/condominios/${condoId}/fondo-reserva/egresos`,
  fondoReservaMovimientos: (condoId: string) => `/condominios/${condoId}/fondo-reserva/movimientos`,
  fondoReservaVerificar: (condoId: string) => `/condominios/${condoId}/fondo-reserva/verificar`,

  // Gastos comunes
  gastosComunes: (condoId: string) => `/condominios/${condoId}/gastos-comunes`,
  gastoComunById: (condoId: string, id: string) => `/condominios/${condoId}/gastos-comunes/${id}`,
  gastoComunCargos: (condoId: string, id: string) =>
    `/condominios/${condoId}/gastos-comunes/${id}/cargos`,
  gastoComunEmitir: (condoId: string, id: string) =>
    `/condominios/${condoId}/gastos-comunes/${id}/emitir`,
  gastoComunAnular: (condoId: string, id: string) =>
    `/condominios/${condoId}/gastos-comunes/${id}/anular`,

  // Cargos del residente autenticado
  misCargos: '/residentes/me/cargos',
  iniciarPagoCargo: (cargoId: string) => `/residentes/me/cargos/${cargoId}/pagar`,
  misUnidadesHabitadas: '/residentes/me/unidades-habitadas',

  // Pagos (webhook mock)
  pagoWebhookMock: '/webhooks/pagos/mock',

  // Asambleas
  asambleas: (condoId: string) => `/condominios/${condoId}/asambleas`,
  asambleaById: (condoId: string, id: string) => `/condominios/${condoId}/asambleas/${id}`,
  asambleaPublicar: (condoId: string, id: string) =>
    `/condominios/${condoId}/asambleas/${id}/publicar`,
  asambleaCerrar: (condoId: string, id: string) =>
    `/condominios/${condoId}/asambleas/${id}/cerrar`,
  asambleaAnular: (condoId: string, id: string) =>
    `/condominios/${condoId}/asambleas/${id}/anular`,
  asambleaExtenderVotacion: (condoId: string, id: string) =>
    `/condominios/${condoId}/asambleas/${id}/extender-votacion`,
  asambleaSegundaCitacion: (condoId: string, id: string) =>
    `/condominios/${condoId}/asambleas/${id}/segunda-citacion`,
  asambleaMaterias: (condoId: string, id: string) =>
    `/condominios/${condoId}/asambleas/${id}/materias`,
  asambleaMateriaById: (condoId: string, id: string, materiaId: string) =>
    `/condominios/${condoId}/asambleas/${id}/materias/${materiaId}`,
  asambleaVotar: (condoId: string, id: string, materiaId: string) =>
    `/condominios/${condoId}/asambleas/${id}/materias/${materiaId}/votar`,
  asambleaMisVotos: (condoId: string, id: string) =>
    `/condominios/${condoId}/asambleas/${id}/mis-votos`,
  asambleaResultados: (condoId: string, id: string) =>
    `/condominios/${condoId}/asambleas/${id}/resultados`,
  asambleaActa: (condoId: string, id: string) => `/condominios/${condoId}/asambleas/${id}/acta`,
  asambleaActaAdjuntos: (condoId: string, id: string) =>
    `/condominios/${condoId}/asambleas/${id}/acta/adjuntos`,
  asambleaActaFirmar: (condoId: string, id: string) =>
    `/condominios/${condoId}/asambleas/${id}/acta/firmar`,
  asambleaActaDescargar: (condoId: string, id: string) =>
    `/condominios/${condoId}/asambleas/${id}/acta/descargar`,
  actasHistorial: (condoId: string) => `/condominios/${condoId}/actas`,

  // Dashboard + audit log
  dashboard: (condoId: string) => `/condominios/${condoId}/dashboard`,
  dashboardFinanciero: (condoId: string) => `/condominios/${condoId}/dashboard/financiero`,
  auditLog: (condoId: string) => `/condominios/${condoId}/audit-log`,

  // Mercado Vecinal (cross-tenant marketplace)
  mercadoListings: '/mercado/listings',
  mercadoListingById: (id: string) => `/mercado/listings/${id}`,
  mercadoMyListings: '/mercado/me/listings',
  mercadoAnalyzePhotos: '/mercado/analyze-photos',
  mercadoSellerProfile: (sellerId: string) => `/mercado/sellers/${sellerId}`,
  mercadoSellerRatings: (sellerId: string) => `/mercado/sellers/${sellerId}/ratings`,
  mercadoThreads: '/mercado/threads',
  mercadoThreadById: (threadId: string) => `/mercado/threads/${threadId}`,
  mercadoThreadMessages: (threadId: string) => `/mercado/threads/${threadId}/messages`,
  mercadoThreadRead: (threadId: string) => `/mercado/threads/${threadId}/read`,

  // Visitas (authed)
  visitas: (condoId: string) => `/condominios/${condoId}/visitas`,
  misVisitas: '/visitas/me',

  // Control de Visitas — eventos (ingresos/egresos)
  visitasIngresosList: (condoId: string) =>
    `/condominios/${condoId}/visitas/ingresos`,
  visitasWalkIn: (condoId: string) =>
    `/condominios/${condoId}/visitas/walk-in`,
  visitasEgreso: (condoId: string, id: string) =>
    `/condominios/${condoId}/visitas/ingresos/${id}/egreso`,
  visitasHistorialUnidad: (condoId: string, unidadId: string) =>
    `/condominios/${condoId}/unidades/${unidadId}/visitas`,

  // Visitas verification (public)
  visitaVerificar: (token: string) => `/visitas/verificar/${token}`,
  visitaMarcarUsada: (token: string) => `/visitas/verificar/${token}/usar`,

  // Admin management (workspace + membership)
  condominioAdmins: (id: string) => `/admin/condominios/${id}/admins`,
  condominioAdmin: (id: string, userId: string) => `/admin/condominios/${id}/admins/${userId}`,
  meTenants: '/me/tenants',
} as const;
